import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { postFetcher } from "../../../api/fetchers";
import { endpoints } from "../../../api/endpoints";
import { useLanguage } from "../../context/LanguageContext";

const ChangePassword: React.FC = () => {
  const { user } = useUser();
  const { t } = useLanguage();

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required(t("changePassword", "i1E")),
      newPassword: Yup.string().required(t("changePassword", "i2E")),
      confirmNewPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), undefined], t("changePassword", "i3E"))
        .required(t("changePassword", "i3E")),
    }),
    onSubmit: async (values) => {
      try {
        const response = await postFetcher(endpoints.user.updatePassword, {
          arg: {
            email: user?.email,
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
          },
        });

        if (response?.result) {
          toast.success(t("toastMessage", "passSuc"));
        } else {
          toast.error(response?.message || t("toastMessage", "passErr"));
        }
      } catch (error) {
        toast.error(t("toastMessage", "passErr"));
        console.error("Error changing password:", error);
      }
    },
  });

  return (
    <div className="p-8 mx-4 h-full shadow-lg rounded-lg bg-white">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Link
        to="/profile/edit"
        className="text-primary-color font-medium text-sm mb-4 inline-block"
      >
        {`< ${t("changePassword", "back")}`}
      </Link>
      <h2 className="text-xl font-bold mb-4">{t("changePassword", "title")}</h2>
      <form className="space-y-4" onSubmit={formik.handleSubmit}>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t("changePassword", "i1")}
          </label>
          <input
            type="password"
            id="oldPassword"
            name="oldPassword"
            className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 ${
              formik.touched.oldPassword && formik.errors.oldPassword
                ? "border-red-500"
                : ""
            }`}
            value={formik.values.oldPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.oldPassword && formik.errors.oldPassword ? (
            <div className="text-red-500 text-sm">
              {formik.errors.oldPassword}
            </div>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t("changePassword", "i2")}
          </label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 ${
              formik.touched.newPassword && formik.errors.newPassword
                ? "border-red-500"
                : ""
            }`}
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.newPassword && formik.errors.newPassword ? (
            <div className="text-red-500 text-sm">
              {formik.errors.newPassword}
            </div>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t("changePassword", "i3")}
          </label>
          <input
            type="password"
            id="confirmNewPassword"
            name="confirmNewPassword"
            className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 ${
              formik.touched.confirmNewPassword &&
              formik.errors.confirmNewPassword
                ? "border-red-500"
                : ""
            }`}
            value={formik.values.confirmNewPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.confirmNewPassword &&
          formik.errors.confirmNewPassword ? (
            <div className="text-red-500 text-sm">
              {formik.errors.confirmNewPassword}
            </div>
          ) : null}
        </div>

        <button
          type="submit"
          className="w-full py-2 px-4 bg-primary-color md:max-w-64 text-white font-bold rounded-md hover:bg-color-pink"
        >
          {t("changePassword", "button")}
        </button>
      </form>
    </div>
  );
};

export default ChangePassword;
